import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  ArrowRight,
  Stethoscope,
  Eye,
  EyeOff,
  Activity,
  Sparkles,
  CheckCircle,
  Mail,
  AlertCircle,
  Brain,
  FileText,
  ShieldCheck,
  Calculator,
  FileEdit,
  BookOpen,
  BadgeCheck,
  Zap,
  Clock,
  UserCircle,
  Star,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { auth } from '../../firebase';
import { signInWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import SEO from '../../components/SEO';

const LOGO_URL = '/RemaH.png';

const AuthProPage = () => {
  const navigate = useNavigate();
  const { login, register, user, role, loading } = useAuth();
  const [formLoading, setFormLoading] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  const [loginData, setLoginData] = useState({ email: '', password: '' });

  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    passwordConfirm: '',
    first_name: '',
    last_name: '',
    birth_year: new Date().getFullYear() - 30,
    profile_type: '',
    rpps: '',
    subscription_type: 'free',
  });

  useEffect(() => {
    if (loading) return;
    if (user && role === 'admin') navigate('/admin/dashboard', { replace: true });
    else if (user?.profile_type === 'infirmier') navigate('/pro/infirmier', { replace: true });
    else if (user?.profile_type === 'medecin') navigate('/pro/medecin', { replace: true });
    else if (user) navigate('/dashboard', { replace: true });
  }, [user, role, loading, navigate]);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getFirebaseErrorMessage = (errorCode) => {
    const errors = {
      'auth/user-not-found': 'Aucun compte associé à cet email',
      'auth/wrong-password': 'Mot de passe incorrect',
      'auth/invalid-email': "Format d'email invalide",
      'auth/user-disabled': 'Ce compte a été désactivé',
      'auth/too-many-requests': 'Trop de tentatives. Réessayez plus tard',
      'auth/invalid-credential': 'Email ou mot de passe incorrect',
      'auth/network-request-failed': 'Erreur de connexion réseau',
      'auth/email-already-in-use': 'Un compte existe déjà avec cet email',
    };
    return errors[errorCode] || 'Erreur de connexion';
  };

  const validatePassword = (password) => {
    if (password.length < 8) return 'Le mot de passe doit contenir au moins 8 caractères';
    if (!/[A-Z]/.test(password)) return 'Le mot de passe doit contenir au moins une majuscule';
    if (!/[a-z]/.test(password)) return 'Le mot de passe doit contenir au moins une minuscule';
    if (!/[0-9]/.test(password)) return 'Le mot de passe doit contenir au moins un chiffre';
    if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password))
      return 'Le mot de passe doit contenir au moins un caractère spécial (!@#$%^&*...)';
    return null;
  };

  const getPasswordStrength = (password) => {
    if (!password) return { score: 0, label: '', color: '' };
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) score++;
    if (score <= 2) return { score, label: 'Faible', color: 'bg-red-500' };
    if (score <= 4) return { score, label: 'Moyen', color: 'bg-yellow-500' };
    return { score, label: 'Fort', color: 'bg-green-500' };
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success('Connexion réussie !');
      navigate('/dashboard');
    } catch (error) {
      if (error.isEmailNotVerified || error.message === 'EMAIL_NOT_VERIFIED') {
        setUserEmail(loginData.email);
        setShowEmailVerification(true);
        toast.error('Veuillez vérifier votre email avant de vous connecter');
      } else if (error.code) {
        toast.error(getFirebaseErrorMessage(error.code));
      } else {
        toast.error(error.message || 'Erreur de connexion');
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (!registerData.profile_type) {
      toast.error('Veuillez sélectionner votre profil professionnel');
      return;
    }

    if (!registerData.rpps || registerData.rpps.length !== 11) {
      toast.error('Le numéro RPPS doit contenir 11 chiffres');
      return;
    }

    const passwordError = validatePassword(registerData.password);
    if (passwordError) { toast.error(passwordError); return; }

    if (registerData.password !== registerData.passwordConfirm) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    setFormLoading(true);
    try {
      await register(
        registerData.email,
        registerData.password,
        registerData.first_name,
        registerData.last_name,
        registerData.birth_year,
        registerData.profile_type,
        registerData.subscription_type,
        { rpps: registerData.rpps, status: 'pending' }
      );
      setUserEmail(registerData.email);
      setShowEmailVerification(true);
      toast.success('Inscription réussie ! Votre compte sera activé sous 24-48h 📧');
    } catch (error) {
      if (error.code) {
        toast.error(getFirebaseErrorMessage(error.code));
      } else {
        toast.error(error.message || "Erreur lors de l'inscription");
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleResendEmail = async () => {
    setFormLoading(true);
    try {
      const tempCredential = await signInWithEmailAndPassword(auth, userEmail, loginData.password || registerData.password);
      await sendEmailVerification(tempCredential.user, {
        url: `${window.location.origin}/auth/pro`,
        handleCodeInApp: false,
      });
      await auth.signOut();
      toast.success('Email de vérification renvoyé ! Vérifiez votre boîte de réception (et vos spams) 📧');
    } catch (error) {
      toast.error("Erreur lors du renvoi de l'email");
    } finally {
      setFormLoading(false);
    }
  };

  if (showEmailVerification) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-green-950 to-emerald-950 relative overflow-hidden flex items-center justify-center px-4">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -left-40 w-96 h-96 bg-gradient-to-br from-green-500/30 to-emerald-500/30 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-teal-500/20 to-cyan-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
        </div>
        <Card className="relative z-10 p-8 bg-white/10 backdrop-blur-xl border-2 border-white/20 shadow-2xl rounded-3xl max-w-md w-full">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Vérifiez votre email</h2>
            <p className="text-white/60 text-sm mb-6">
              Un email de vérification a été envoyé à{' '}
              <span className="text-green-400">{userEmail}</span>
            </p>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6">
              <p className="text-white/80 text-sm mb-2">📬 Vérifiez votre boîte de réception</p>
              <p className="text-white/50 text-xs">
                Cliquez sur le lien dans l'email pour activer votre compte, puis revenez vous connecter.
              </p>
              <p className="text-white/80 text-sm mt-3 font-medium">
                📁 Pensez à regarder dans vos courriers indésirables (spam) : c'est souvent là que cet email arrive.
              </p>
            </div>
            <Button onClick={handleResendEmail} disabled={formLoading} className="w-full bg-white/10 hover:bg-white/20 text-white border border-white/20 mb-4">
              {formLoading ? 'Envoi...' : "Renvoyer l'email"}
            </Button>
            <Button onClick={() => setShowEmailVerification(false)} variant="ghost" className="w-full text-white/60 hover:text-white hover:bg-white/5">
              Retour à la connexion
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-green-950 to-emerald-950 relative overflow-hidden">
      <SEO
        title="Espace Professionnel — Connexion"
        description="Accédez à l'espace professionnel Remadia pour médecins et infirmiers."
        path="/auth/pro"
      />

      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute -top-40 -left-40 w-96 h-96 bg-gradient-to-br from-green-500/30 to-emerald-500/30 rounded-full blur-3xl animate-pulse"
          style={{ transform: `translateY(${scrollY * 0.1}px)` }}
        />
        <div
          className="absolute top-1/4 right-0 w-[600px] h-[600px] bg-gradient-to-br from-teal-500/20 to-emerald-500/20 rounded-full blur-3xl animate-pulse"
          style={{ transform: `translateY(${scrollY * -0.15}px)`, animationDelay: '2s' }}
        />
        <div
          className="absolute bottom-0 left-1/3 w-[500px] h-[500px] bg-gradient-to-br from-green-600/20 to-teal-500/20 rounded-full blur-3xl animate-pulse"
          style={{ transform: `translateY(${scrollY * 0.2}px)`, animationDelay: '1s' }}
        />

        {/* Floating particles */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-green-400/20 rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${5 + Math.random() * 10}s`,
            }}
          />
        ))}

        {/* Grid overlay */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjAzIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30" />
      </div>

      <div className="relative z-10">
        {/* Navbar */}
        <nav className="fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 py-3 sm:py-4 bg-slate-950/60 backdrop-blur-xl border-b border-white/10">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <img src={LOGO_URL} alt="Remadia" className="w-8 h-8 sm:w-10 sm:h-10 object-contain" />
              <span className="text-lg sm:text-xl font-semibold tracking-wide bg-gradient-to-r from-white to-gray-300 text-transparent bg-clip-text drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]">
                Remadia <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400">Pro</span>
              </span>
            </div>
            <Button
              onClick={() => document.getElementById('auth-section')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white px-4 sm:px-6 py-2 rounded-xl text-xs sm:text-sm font-semibold shadow-[0_0_20px_rgba(34,197,94,0.2)]"
            >
              <span className="hidden sm:inline">Connexion / Inscription</span>
              <span className="sm:hidden">Connexion</span>
            </Button>
          </div>
        </nav>

        {/* HERO SECTION */}
        <section className="relative min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 pt-20 pb-24 overflow-hidden text-center">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-500/10 backdrop-blur-md border border-green-500/20 rounded-full mb-8 animate-fade-in mx-auto shadow-[0_0_15px_rgba(34,197,94,0.15)]">
            <BadgeCheck className="w-4 h-4 text-green-400 animate-pulse" />
            <span className="text-sm text-green-100 font-semibold tracking-wide uppercase">
              Réservé aux professionnels de santé
            </span>
          </div>

          {/* Logo */}
          <div className="relative mb-10 group mx-auto">
            <div className="absolute inset-0 bg-gradient-to-r from-green-500/50 via-emerald-500/50 to-teal-500/50 rounded-full blur-[80px] opacity-70 group-hover:opacity-100 transition-opacity duration-700 animate-pulse" />
            <div className="relative w-56 h-56 md:w-72 md:h-72 lg:w-80 lg:h-80 flex items-center justify-center transform group-hover:scale-105 transition-transform duration-500">
              <img
                src={LOGO_URL}
                alt="Remadia Pro"
                className="w-full h-full object-contain drop-shadow-[0_0_30px_rgba(34,197,94,0.4)]"
              />
            </div>
          </div>

          {/* Titles */}
          <div className="max-w-4xl mx-auto mb-12">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-white mb-6 leading-tight">
              L'intelligence artificielle au service{' '}
              <br className="hidden md:block" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 via-emerald-400 to-teal-400">
                de votre pratique
              </span>
            </h1>
            <p className="text-lg md:text-xl text-green-100/80 max-w-2xl mx-auto leading-relaxed font-light">
              Gagnez du temps médical, allégez votre charge mentale et accédez instantanément à la littérature scientifique et aux recommandations à jour.
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 max-w-4xl mx-auto mb-14 w-full px-4">
            <div className="flex flex-col items-center justify-center bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-5 hover:bg-white/10 hover:border-green-500/30 transition-all duration-300 transform hover:-translate-y-1">
              <Zap className="w-6 h-6 text-green-400 mb-2" />
              <div className="text-2xl font-bold text-white mb-1">Instantané</div>
              <div className="text-xs text-green-200/70 font-medium tracking-wider uppercase">Réponses IA</div>
            </div>
            <div className="flex flex-col items-center justify-center bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-5 hover:bg-white/10 hover:border-emerald-500/30 transition-all duration-300 transform hover:-translate-y-1">
              <ShieldCheck className="w-6 h-6 text-emerald-400 mb-2" />
              <div className="text-2xl font-bold text-white mb-1">100%</div>
              <div className="text-xs text-green-200/70 font-medium tracking-wider uppercase">Confidentiel</div>
            </div>
            <div className="flex flex-col items-center justify-center bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-5 hover:bg-white/10 hover:border-teal-500/30 transition-all duration-300 transform hover:-translate-y-1">
              <Clock className="w-6 h-6 text-teal-400 mb-2" />
              <div className="text-2xl font-bold text-white mb-1">24/7</div>
              <div className="text-xs text-green-200/70 font-medium tracking-wider uppercase">Disponible</div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-5 justify-center items-center w-full max-w-md sm:max-w-none mx-auto">
            <Button
              onClick={() => document.getElementById('auth-section')?.scrollIntoView({ behavior: 'smooth' })}
              size="lg"
              className="group relative w-full sm:w-auto bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white text-lg font-medium px-8 py-6 shadow-[0_0_30px_rgba(34,197,94,0.3)] hover:shadow-[0_0_40px_rgba(34,197,94,0.5)] transition-all transform hover:-translate-y-0.5 border-0 rounded-2xl overflow-hidden"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 ease-in-out" />
              <span className="relative flex items-center justify-center gap-2">
                Accéder à mon espace Pro
                <Sparkles className="w-5 h-5" />
              </span>
            </Button>
            <Button
              onClick={() => document.getElementById('features-section')?.scrollIntoView({ behavior: 'smooth' })}
              size="lg"
              variant="outline"
              className="group w-full sm:w-auto bg-white/5 backdrop-blur-md border-2 border-white/20 hover:border-white/40 hover:bg-white/10 text-white text-lg font-medium px-8 py-6 rounded-2xl transition-all transform hover:-translate-y-0.5"
            >
              Découvrir les outils
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce opacity-70 hover:opacity-100 transition-opacity cursor-pointer">
            <div className="w-7 h-11 border-2 border-white/30 rounded-full flex justify-center pt-2">
              <div className="w-1 h-2.5 bg-white/80 rounded-full animate-pulse" />
            </div>
          </div>
        </section>

        {/* FEATURES SECTION */}
        <section id="features-section" className="bg-gradient-to-b from-transparent via-green-950/50 to-transparent py-32 backdrop-blur-sm">
          <div className="container mx-auto px-6">
            <div className="text-center mb-20">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/20 rounded-full mb-6">
                <Star className="w-4 h-4 text-green-400" />
                <span className="text-sm text-green-300 font-medium">Outils Professionnels</span>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold mb-6">
                <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                  Une suite clinique complète
                </span>
              </h2>
              <p className="text-xl text-green-200/80 max-w-3xl mx-auto">
                Des outils conçus par et pour les professionnels de santé, pour optimiser chaque instant de votre pratique
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
              <Card className="group relative p-8 bg-gradient-to-br from-green-500/10 to-emerald-600/10 hover:from-green-500/20 hover:to-emerald-600/20 border-2 border-green-500/20 hover:border-green-400/40 backdrop-blur-md transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative z-10">
                  <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mb-6 shadow-xl group-hover:shadow-green-500/50 transition-shadow transform group-hover:rotate-6">
                    <Brain className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-green-300 transition-colors">
                    Aide à l'évaluation clinique
                  </h3>
                  <p className="text-green-200/80 leading-relaxed mb-4">
                    Soumettez un tableau clinique complexe. L'IA croise les symptômes avec les dernières recommandations HAS et sociétés savantes.
                  </p>
                  <ul className="space-y-2">
                    {['Analyse des symptômes', 'Recommandations HAS', 'Scores validés'].map((item) => (
                      <li key={item} className="flex items-center gap-2 text-sm text-green-300">
                        <CheckCircle className="w-4 h-4" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>

              <Card className="group relative p-8 bg-gradient-to-br from-emerald-500/10 to-teal-600/10 hover:from-emerald-500/20 hover:to-teal-600/20 border-2 border-emerald-500/20 hover:border-emerald-400/40 backdrop-blur-md transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative z-10">
                  <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mb-6 shadow-xl group-hover:shadow-emerald-500/50 transition-shadow transform group-hover:rotate-6">
                    <Calculator className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-emerald-300 transition-colors">
                    Calculateurs & Perfusion
                  </h3>
                  <p className="text-green-200/80 leading-relaxed mb-4">
                    Hub ultra-rapide pour calculer les débits (gouttes/min, PSE), les conversions de doses, et vérifier les compatibilités en Y.
                  </p>
                  <ul className="space-y-2">
                    {['Débits & PSE', 'Conversions doses', 'Compatibilités IV'].map((item) => (
                      <li key={item} className="flex items-center gap-2 text-sm text-emerald-300">
                        <CheckCircle className="w-4 h-4" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>

              <Card className="group relative p-8 bg-gradient-to-br from-teal-500/10 to-green-600/10 hover:from-teal-500/20 hover:to-green-600/20 border-2 border-teal-500/20 hover:border-teal-400/40 backdrop-blur-md transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-teal-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative z-10">
                  <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-teal-500 to-green-600 flex items-center justify-center mb-6 shadow-xl group-hover:shadow-teal-500/50 transition-shadow transform group-hover:rotate-6">
                    <FileEdit className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-teal-300 transition-colors">
                    Assistant de Transmissions
                  </h3>
                  <p className="text-green-200/80 leading-relaxed mb-4">
                    Dictez vos notes en vrac, l'IA les reformule automatiquement au format standardisé D.A.R. prêtes pour le dossier patient.
                  </p>
                  <ul className="space-y-2">
                    {['Format D.A.R.', 'Reformulation IA', 'Dossier patient'].map((item) => (
                      <li key={item} className="flex items-center gap-2 text-sm text-teal-300">
                        <CheckCircle className="w-4 h-4" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            </div>

            {/* Additional features grid */}
            <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto mt-8">
              {[
                { icon: FileText, title: 'Analyse Biologie & Courriers', desc: 'Extrayez les valeurs anormales et synthétisez les courriers d\'hospitalisation.', color: 'green' },
                { icon: BookOpen, title: 'Bibliothèque & Protocoles', desc: 'Accès immédiat à la littérature médicale et aux scores validés.', color: 'emerald' },
                { icon: ShieldCheck, title: 'Conformité & Sécurité', desc: 'Vos requêtes ne sont pas utilisées pour entraîner des modèles publics.', color: 'teal' },
              ].map((item) => (
                <div
                  key={item.title}
                  className={`p-6 bg-white/5 backdrop-blur-sm border border-${item.color}-500/20 rounded-2xl hover:bg-white/10 transition-all group transform hover:scale-105`}
                >
                  <item.icon className={`w-8 h-8 text-${item.color}-400 mb-3 group-hover:scale-110 transition-transform`} />
                  <h4 className="text-white font-semibold mb-2">{item.title}</h4>
                  <p className="text-white/50 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="bg-gradient-to-b from-transparent via-green-950/30 to-transparent py-32 backdrop-blur-sm">
          <div className="container mx-auto px-6">
            <div className="text-center mb-20">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/20 rounded-full mb-6">
                <Zap className="w-4 h-4 text-green-400" />
                <span className="text-sm text-green-300 font-medium">Simple & Efficace</span>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold mb-6">
                <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                  Comment ça marche ?
                </span>
              </h2>
              <p className="text-xl text-green-200/80 max-w-3xl mx-auto">
                Trois étapes pour accéder à l'intelligence clinique de demain
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-12 max-w-6xl mx-auto relative">
              <div className="hidden md:block absolute top-1/3 left-0 right-0 h-0.5 bg-gradient-to-r from-green-500/50 via-emerald-500/50 to-teal-500/50" />
              {[
                {
                  number: '1',
                  icon: UserCircle,
                  title: 'Créez votre compte Pro',
                  description: 'Inscription réservée aux médecins et infirmiers. Choisissez votre profil pour accéder aux outils adaptés à votre spécialité.',
                  color: 'from-green-500 to-emerald-600',
                },
                {
                  number: '2',
                  icon: Brain,
                  title: 'Soumettez vos cas',
                  description: 'Notre IA clinique analyse vos tableaux, calcule vos débits et reformate vos transmissions en temps réel.',
                  color: 'from-emerald-500 to-teal-600',
                },
                {
                  number: '3',
                  icon: Activity,
                  title: 'Optimisez votre pratique',
                  description: 'Accédez aux recommandations à jour, épinglez vos protocoles et gagnez un temps médical précieux.',
                  color: 'from-teal-500 to-green-600',
                },
              ].map((step) => (
                <div key={step.number} className="relative text-center group">
                  <div className="relative inline-block mb-8">
                    <div className={`absolute inset-0 bg-gradient-to-br ${step.color} rounded-full blur-xl opacity-50 group-hover:opacity-75 transition-opacity`} />
                    <div className={`relative w-24 h-24 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center shadow-2xl transform group-hover:scale-110 transition-transform`}>
                      <span className="text-4xl font-bold text-white">{step.number}</span>
                    </div>
                  </div>
                  <div className="mb-6 flex justify-center">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center transform group-hover:rotate-6 transition-transform`}>
                      <step.icon className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-green-300 transition-colors">
                    {step.title}
                  </h3>
                  <p className="text-green-200/80 leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* AUTH SECTION */}
        <section id="auth-section" className="py-32 relative">
          <div className="container mx-auto px-6">
            <div className="max-w-md mx-auto">
              <div className="absolute inset-0 bg-gradient-to-r from-green-500/20 via-emerald-500/20 to-teal-500/20 rounded-3xl blur-3xl" />

              <Card className="relative p-8 bg-white/10 backdrop-blur-xl border-2 border-white/20 shadow-2xl rounded-3xl">
                <Tabs defaultValue="login" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-8 bg-white/5 p-1 rounded-xl">
                    <TabsTrigger value="login" className="rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-500 data-[state=active]:to-emerald-600 data-[state=active]:text-white transition-all">
                      Connexion
                    </TabsTrigger>
                                    <TabsTrigger
                      value="register"
                      className="rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-500 data-[state=active]:to-emerald-600 data-[state=active]:text-white transition-all"
                    >
                      Inscription
                    </TabsTrigger>
                  </TabsList>

                  {/* Login */}
                  <TabsContent value="login">
                    <form onSubmit={handleLogin} className="space-y-5">
                      <div>
                        <Label htmlFor="pro-login-email" className="text-white mb-2 block">Email professionnel</Label>
                        <Input
                          id="pro-login-email"
                          type="email"
                          placeholder="medecin@hopital.fr"
                          value={loginData.email}
                          onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                          required
                          className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-green-400 focus:ring-green-400/20 rounded-xl"
                        />
                      </div>
                      <div>
                        <Label htmlFor="pro-login-password" className="text-white mb-2 block">Mot de passe</Label>
                        <div className="relative">
                          <Input
                            id="pro-login-password"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="••••••••"
                            value={loginData.password}
                            onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                            required
                            className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-green-400 focus:ring-green-400/20 rounded-xl pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white"
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <button
                          type="button"
                          onClick={() => navigate('/forgot-password')}
                          className="text-sm text-green-400 hover:text-green-300 transition-colors float-right"
                        >
                          Mot de passe oublié ?
                        </button>
                      </div>
                      <Button
                        type="submit"
                        disabled={formLoading}
                        className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white shadow-lg hover:shadow-green-500/50 py-6 text-lg rounded-xl transition-all transform hover:scale-105"
                      >
                        {formLoading ? 'Connexion...' : 'Se connecter'}
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                    </form>
                  </TabsContent>

                  {/* Register */}
                  <TabsContent value="register">
                    <form onSubmit={handleRegister} className="space-y-5">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="pro-first-name" className="text-white mb-2 block">Prénom</Label>
                          <Input id="pro-first-name" placeholder="Jean" value={registerData.first_name} onChange={(e) => setRegisterData({ ...registerData, first_name: e.target.value })} required className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-green-400 focus:ring-green-400/20 rounded-xl" />
                        </div>
                        <div>
                          <Label htmlFor="pro-last-name" className="text-white mb-2 block">Nom</Label>
                          <Input id="pro-last-name" placeholder="Dupont" value={registerData.last_name} onChange={(e) => setRegisterData({ ...registerData, last_name: e.target.value })} required className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-green-400 focus:ring-green-400/20 rounded-xl" />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="pro-email" className="text-white mb-2 block">Email professionnel</Label>
                        <Input id="pro-email" type="email" placeholder="medecin@hopital.fr" value={registerData.email} onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })} required className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-green-400 focus:ring-green-400/20 rounded-xl" />
                      </div>

                      <div>
                        <Label className="flex items-center gap-2 text-white mb-2">
                          <span>Profession</span>
                          <span className="text-green-400">*</span>
                        </Label>
                        <div className="grid grid-cols-2 gap-3">
                          {[
                            { value: 'medecin', label: 'Médecin', icon: Stethoscope },
                            { value: 'infirmier', label: 'Infirmier(ère)', icon: Activity },
                          ].map(({ value, label, icon: Icon }) => (
                            <button
                              key={value}
                              type="button"
                              onClick={() => setRegisterData({ ...registerData, profile_type: value })}
                              className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${registerData.profile_type === value ? 'border-green-400 bg-green-500/20 text-white' : 'border-white/20 bg-white/5 text-white/60 hover:border-white/40 hover:text-white'}`}
                            >
                              <Icon className="w-6 h-6" />
                              <span className="text-sm font-medium">{label}</span>
                              {registerData.profile_type === value && <CheckCircle className="w-4 h-4 text-green-400" />}
                            </button>
                          ))}
                        </div>
                        <div className="mt-3 flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                          <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-amber-200">
                            <strong>Important :</strong> Ce choix est définitif et déterminera votre espace professionnel.
                          </p>
                        </div>
                      </div>

                      {registerData.profile_type && (
                        <div>
                          <Label htmlFor="pro-rpps" className="text-white mb-2 block">
                            Numéro RPPS <span className="text-green-400">*</span>
                          </Label>
                          <Input
                            id="pro-rpps"
                            type="text"
                            placeholder="10012345678"
                            value={registerData.rpps}
                            onChange={(e) => setRegisterData({ ...registerData, rpps: e.target.value })}
                            required
                            maxLength={11}
                            className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-green-400 focus:ring-green-400/20 rounded-xl"
                          />
                          <div className="mt-2 flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                            <AlertCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-blue-200">
                              Votre compte sera activé sous <strong>24-48h</strong> après vérification de votre numéro RPPS. Vous recevrez un email de confirmation.
                            </p>
                          </div>
                        </div>
                      )}

                      <div>
                        <Label htmlFor="pro-password" className="text-white mb-2 block">Mot de passe</Label>
                        <div className="relative">
                          <Input id="pro-password" type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={registerData.password} onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })} required className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-green-400 focus:ring-green-400/20 rounded-xl pr-10" />
                          <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white">
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        {registerData.password && (() => {
                          const strength = getPasswordStrength(registerData.password);
                          const criteria = [
                            { label: '8 caractères minimum', met: registerData.password.length >= 8 },
                            { label: '1 majuscule', met: /[A-Z]/.test(registerData.password) },
                            { label: '1 minuscule', met: /[a-z]/.test(registerData.password) },
                            { label: '1 chiffre', met: /[0-9]/.test(registerData.password) },
                            { label: '1 caractère spécial', met: /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(registerData.password) },
                          ];
                          return (
                            <div className="mt-2 space-y-2">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-1.5 bg-white/20 rounded-full overflow-hidden flex gap-0.5">
                                  {[1,2,3,4,5].map((i) => <div key={i} className={`flex-1 h-full rounded-full transition-all duration-300 ${i <= strength.score ? strength.color : 'bg-white/10'}`} />)}
                                </div>
                                <span className={`text-xs font-medium ${strength.score <= 2 ? 'text-red-400' : strength.score <= 4 ? 'text-yellow-400' : 'text-green-400'}`}>{strength.label}</span>
                              </div>
                              <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                                {criteria.map((c) => <span key={c.label} className={`text-xs flex items-center gap-1 ${c.met ? 'text-green-400' : 'text-white/40'}`}>{c.met ? '✅' : '❌'} {c.label}</span>)}
                              </div>
                            </div>
                          );
                        })()}
                      </div>

                      <div>
                        <Label htmlFor="pro-password-confirm" className="text-white mb-2 block">Confirmer le mot de passe</Label>
                        <div className="relative">
                          <Input id="pro-password-confirm" type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={registerData.passwordConfirm} onChange={(e) => setRegisterData({ ...registerData, passwordConfirm: e.target.value })} required className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-green-400 focus:ring-green-400/20 rounded-xl pr-10" />
                          <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white">
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      <Button type="submit" disabled={formLoading || !registerData.profile_type} className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white shadow-lg py-6 text-lg rounded-xl transition-all transform hover:scale-105 disabled:opacity-50">
                        {formLoading ? 'Inscription...' : 'Créer mon compte Pro'}
                        <Sparkles className="ml-2 w-5 h-5" />
                      </Button>
                    </form>
                  </TabsContent>
                </Tabs>

                <div className="mt-6 pt-5 border-t border-white/10 text-center space-y-3">
                  <p className="text-white/50 text-sm">
                    Vous êtes patient ?{' '}
                    <button onClick={() => navigate('/auth')} className="text-green-400 hover:text-green-300 font-medium underline underline-offset-2 transition-colors">
                      Accéder à l'espace patient
                    </button>
                  </p>
                  <p className="text-white/25 text-xs">
                    Remadia Pro est une marque{' '}
                    <a
                      href="https://vandomia.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white/40 hover:text-white/70 transition-colors"
                    >
                      Vandomia
                    </a>
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </section>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); opacity: 0.2; }
          50% { transform: translateY(-20px); opacity: 0.5; }
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-float { animation: float linear infinite; }
        .animate-fade-in { animation: fade-in 0.6s ease-out; }
      `}</style>
    </div>
  );
};

export default AuthProPage;
